import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTrialStore } from '../store/trialStore';
import api from '../utils/api';

/**
 * Sticky banner at top of screen showing trial countdown.
 * Only renders when user is in active trial. Becomes urgent (red) at ≤2 days.
 * Updates every minute.
 */
const TrialCountdownBanner = () => {
  const { inTrial, trialEndsAt, daysRemaining, fetchLockState } = useTrialStore();
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0 });
  const [buying, setBuying] = useState(false);

  // Convert trial → paid Pro with the −30% first-month offer. Uses the same
  // gated buy-now endpoint as the Step5 secondary CTA (auth + rejects locked /
  // already-paying users; renewal charges full price). Previously this CTA
  // deep-linked /subscription?promo=ONBOARDING_BUY_NOW, but that code is a
  // VIRTUAL promo (not a real PromoCode), so the tariff page's /api/promo/validate
  // call 400'd with «Промокод не найден». Going straight to checkout avoids that.
  const handleBuyPro = async () => {
    if (buying) return;
    setBuying(true);
    try {
      const res = await api.post('/api/onboarding/buy-now', { planId: 'professional' });
      localStorage.setItem('lastPaymentId', res.data.data.paymentId);
      window.location.href = res.data.data.confirmationUrl;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Не удалось создать платёж. Попробуй ещё раз.');
      setBuying(false);
    }
  };

  useEffect(() => {
    fetchLockState();
  }, [fetchLockState]);

  useEffect(() => {
    if (!inTrial || !trialEndsAt) return;
    const tick = () => {
      const diff = new Date(trialEndsAt) - Date.now();
      if (diff <= 0) {
        fetchLockState(); // re-check
        return;
      }
      const days = Math.floor(diff / (24 * 3600 * 1000));
      const hours = Math.floor((diff % (24 * 3600 * 1000)) / (3600 * 1000));
      setTimeLeft({ days, hours });
    };
    tick();
    const interval = setInterval(tick, 60 * 1000);
    return () => clearInterval(interval);
  }, [inTrial, trialEndsAt, fetchLockState]);

  if (!inTrial) return null;

  const urgent = daysRemaining <= 2;
  const dayWord = (n) => {
    const m10 = n % 10, m100 = n % 100;
    if (m10 === 1 && m100 !== 11) return 'день';
    if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return 'дня';
    return 'дней';
  };

  return (
    <div className="fixed top-3 left-1/2 -translate-x-1/2 z-50 max-w-[92vw] flex items-center gap-2.5 bg-zinc-900/95 backdrop-blur border border-zinc-700/80 rounded-full shadow-2xl shadow-black/40 pl-4 pr-1.5 py-1.5">
      <span
        className={`w-2 h-2 rounded-full flex-shrink-0 animate-pulse ${urgent ? 'bg-red-400' : 'bg-lime-400'}`}
      />
      <span className="text-sm text-zinc-200 whitespace-nowrap truncate">
        <span className="font-semibold text-white">Pro активен</span>
        <span className="text-zinc-600"> · </span>
        {urgent ? (
          <span className="text-red-400 font-medium">
            <span className="hidden sm:inline">триал заканчивается — </span>осталось {timeLeft.days > 0 ? `${timeLeft.days}д ` : ''}{timeLeft.hours}ч
          </span>
        ) : (
          <span><span className="hidden sm:inline">бесплатно ещё </span>{timeLeft.days} {dayWord(timeLeft.days)}</span>
        )}
      </span>
      <button
        onClick={handleBuyPro}
        disabled={buying}
        className="flex-shrink-0 bg-lime-400 hover:bg-lime-300 text-zinc-900 text-xs font-bold px-3.5 py-1.5 rounded-full whitespace-nowrap transition-colors disabled:opacity-60"
      >
        {buying ? 'Создаём платёж…' : 'Оформить Pro · −30%'}
      </button>
    </div>
  );
};

export default TrialCountdownBanner;
